import { CATALOG_FRAMEWORK_META } from '@kirin/api-client/contracts/ai-instruction-catalog';
import { Severity } from '@kirin/api-client/contracts/shared/severity';
import type { QueryListFilter } from '@kirin/api-client/contracts/extensions';
import type {
  CoverageStatus,
  ProjectCoverageCategory,
} from '@kirin/api-client/contracts/projects';

/**
 * Client-side filtering + sorting for the single-project coverage table
 * (KIR-3517). The whole category set is already in memory, so this is pure data
 * transformation — no server round-trip — kept out of the component file so it
 * unit-tests cleanly and doesn't trip the fast-refresh rule.
 */

/** The columns that carry a filter/sort. `#` is display-only. */
export type CoverageField =
  | 'name'
  | 'severity'
  | 'framework'
  | 'status'
  | 'coveredFilePath'
  | 'instruction';

export const SEVERITY_LABEL: Record<Severity, string> = {
  [Severity.Critical]: 'Critical',
  [Severity.High]: 'High',
  [Severity.Medium]: 'Medium',
  [Severity.Low]: 'Low',
};
const SEVERITY_ORDER: Record<Severity, number> = {
  [Severity.Critical]: 0,
  [Severity.High]: 1,
  [Severity.Medium]: 2,
  [Severity.Low]: 3,
};
/** Ordered severity labels for the filter dropdown (most severe first). */
const SEVERITY_LABELS_ORDERED = [
  SEVERITY_LABEL[Severity.Critical],
  SEVERITY_LABEL[Severity.High],
  SEVERITY_LABEL[Severity.Medium],
  SEVERITY_LABEL[Severity.Low],
];

type StatusKey = 'covered' | 'partial' | 'missing' | 'notScanned';
const STATUS_LABEL: Record<StatusKey, string> = {
  missing: 'Missing',
  partial: 'Partial',
  notScanned: 'Not scanned',
  covered: 'Covered',
};
/** Sort/filter ordering: missing + partial first, covered last (ticket order). */
const STATUS_ORDER: Record<StatusKey, number> = {
  missing: 0,
  partial: 1,
  notScanned: 2,
  covered: 3,
};
const STATUS_LABELS_ORDERED: string[] = (
  ['missing', 'partial', 'notScanned', 'covered'] as StatusKey[]
).map(k => STATUS_LABEL[k]);

function statusKey(status: CoverageStatus | null): StatusKey {
  return status === null ? 'notScanned' : status;
}

/** A multi-select column: the human label it filters + sorts on per category. */
export interface MultiSelectField {
  id: Exclude<CoverageField, 'instruction'>;
  header: string;
  label: (c: ProjectCoverageCategory) => string;
}

export const MULTISELECT_FIELDS: MultiSelectField[] = [
  { id: 'name', header: 'Security category', label: c => c.name },
  {
    id: 'severity',
    header: 'Severity',
    label: c => SEVERITY_LABEL[c.severity as Severity],
  },
  {
    id: 'framework',
    header: 'Framework',
    label: c => CATALOG_FRAMEWORK_META[c.framework].short,
  },
  {
    id: 'status',
    header: 'Status',
    label: c => STATUS_LABEL[statusKey(c.status)],
  },
  {
    id: 'coveredFilePath',
    header: 'Covered in file',
    label: c => c.coveredFilePath ?? '—',
  },
];

const MULTISELECT_BY_ID = new Map(MULTISELECT_FIELDS.map(m => [m.id, m]));

function compareBy(
  field: CoverageField,
  a: ProjectCoverageCategory,
  b: ProjectCoverageCategory
): number {
  switch (field) {
    case 'severity':
      return (
        SEVERITY_ORDER[a.severity as Severity] -
        SEVERITY_ORDER[b.severity as Severity]
      );
    case 'status':
      return (
        STATUS_ORDER[statusKey(a.status)] - STATUS_ORDER[statusKey(b.status)]
      );
    case 'framework':
      return (
        CATALOG_FRAMEWORK_META[a.framework].short.localeCompare(
          CATALOG_FRAMEWORK_META[b.framework].short
        ) || (a.reference ?? '').localeCompare(b.reference ?? '')
      );
    case 'coveredFilePath':
      return (a.coveredFilePath ?? '').localeCompare(b.coveredFilePath ?? '');
    case 'instruction':
      return a.instruction.localeCompare(b.instruction, undefined, {
        sensitivity: 'base',
      });
    case 'name':
    default:
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  }
}

/** Filter + sort state for the coverage table. */
export interface CoverageView {
  /** Active multi-select filters, keyed by column field. */
  filters: Record<string, QueryListFilter>;
  /** Free-text "contains" filter over the "Example instruction to add" column. */
  instructionSearch: string;
  /** Table-wide free-text search across every column. */
  globalSearch: string;
  /** Active sort, or null for the backend's default order. */
  sort: { field: CoverageField; dir: 'asc' | 'desc' } | null;
}

/** Lowercased haystack of every visible column value, for the table-wide search. */
function rowSearchText(c: ProjectCoverageCategory): string {
  const parts = MULTISELECT_FIELDS.map(m => m.label(c));
  parts.push(c.reference ?? '', c.instruction);
  return parts.join(' ').toLowerCase();
}

/**
 * Apply a {@link CoverageView} to the category rows: multi-select include/exclude
 * filters, an instruction substring search, then an optional sort. Pure — never
 * mutates the input array.
 */
export function filterAndSortCategories(
  categories: ProjectCoverageCategory[],
  view: CoverageView
): ProjectCoverageCategory[] {
  let rows = categories;

  for (const filter of Object.values(view.filters)) {
    if (filter.values.length === 0) continue;
    const meta = MULTISELECT_BY_ID.get(filter.field as MultiSelectField['id']);
    if (!meta) continue;
    rows = rows.filter(c => {
      const inSet = filter.values.includes(meta.label(c));
      return filter.condition === 'exclude' ? !inSet : inSet;
    });
  }

  const instructionQuery = view.instructionSearch.trim().toLowerCase();
  if (instructionQuery) {
    rows = rows.filter(c =>
      c.instruction.toLowerCase().includes(instructionQuery)
    );
  }

  const globalQuery = view.globalSearch.trim().toLowerCase();
  if (globalQuery) {
    rows = rows.filter(c => rowSearchText(c).includes(globalQuery));
  }

  if (view.sort) {
    const { field, dir } = view.sort;
    const sign = dir === 'asc' ? 1 : -1;
    rows = [...rows].sort((a, b) => sign * compareBy(field, a, b));
  }

  return rows;
}

/** Distinct filter options for a column, ordered for its dropdown. */
export function distinctOptions(
  categories: ProjectCoverageCategory[],
  meta: MultiSelectField
): string[] {
  const labels = [...new Set(categories.map(meta.label))];
  const ordered =
    meta.id === 'severity'
      ? SEVERITY_LABELS_ORDERED
      : meta.id === 'status'
        ? STATUS_LABELS_ORDERED
        : null;
  if (ordered) {
    return labels.sort((a, b) => ordered.indexOf(a) - ordered.indexOf(b));
  }
  return labels.sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
}
